/**
 * Hidden features
 *
 * Central switch for UI entry points that are unbuilt, placeholders, show
 * made-up numbers, or report success without doing anything. Each flag hides
 * one entry point; the underlying components, routes and data are untouched.
 *
 * To restore an item, set its flag to `false`.
 *
 * Source: docs/audit/2026-09-23-REMEDIATION-PLAN.md (Phase 2/3 "build or hide").
 */
export const HIDDEN_FEATURES = {
  // --- Placeholders and unbuilt screens ---------------------------------------------------------
  /** Dashboard "Reports" card → /reports renders a bare <div>Reports</div> (App.tsx). */
  dashboardReportsCard: true,
  /** Dashboard "Statistics" card → /stats renders "Statistics - Coming Soon" (App.tsx). */
  dashboardStatisticsCard: true,
  /** HQ Dashboard "Marketing Overview" quick action → /admin/marketing-overview "Coming Soon". */
  hqMarketingOverview: true,
  /** Partner Settings → Lead Forms "View Stats" → /admin/lead-forms/:id/submissions (no route). */
  partnerLeadFormViewStats: true,
  /** Partner Management "View" → /admin/partners/:id (no route). */
  partnerManagementView: true,
  /** PartnerHQ "Manage Agents" menu: every item shows "Component not found" or "Coming Soon". */
  partnerHqManageAgents: true,
  /** PartnerHQ "Manually Assign Lead" → "Manual Lead Assignment - Coming Soon". */
  partnerHqManualLeadAssign: true,
  /** PartnerHQ "Recalculate Distribution" → "Coming Soon" toast. */
  partnerHqRecalculateRoundRobin: true,
  /** PartnerHQ "Campaign Templates" → "Campaign Templates - Coming Soon". */
  partnerHqCampaignTemplates: true,
  /** HQ menu Payments → "Pay Partner" → "Pay Partner form coming soon" (PropCloudHQ.tsx). */
  hqPayPartner: true,
  /** Viewing quick actions "View all N overdue reports" → /viewing/reports/overdue (no route). */
  viewingOverdueReportsLink: true,
  /** Viewing "Submit Report" shortcuts → /viewing/report/submit → "Booking not found". */
  viewingSubmitReportShortcut: true,

  // --- Made-up numbers shown as real -------------------------------------------------------------
  /** Lead Analytics → Sources: conversion, avg deal value and trend are Math.random(). */
  leadAnalyticsSourcesRandomMetrics: true,
  /** Activities page "Activity Overview" stats are hard-coded and its three buttons do nothing. */
  activitiesPlaceholderStats: true,
  /** SMS replies show a hard-coded "85% confidence" when no real confidence exists. */
  smsReplyFallbackConfidence: true,

  // --- Buttons that report success without doing anything ---------------------------------------
  /** Workflow list "Duplicate" toasts success but duplicates nothing. */
  workflowDuplicate: true,
  /** My Documents "Resend email" toasts "would be resent" and sends nothing. */
  documentResend: true,
  /** Contact details "Create Deal from Reservation" only shows a toast. */
  createDealFromReservation: true,
} as const;

export type HiddenFeature = keyof typeof HIDDEN_FEATURES;

export const isHidden = (feature: HiddenFeature): boolean => HIDDEN_FEATURES[feature];
